import { useState } from 'react';

import { eachDayOfInterval, format } from 'date-fns';
import { groupBy, values } from 'lodash';

import {
  Employee,
  EmployeeDays
} from 'components/shared/roster/RosterViewByEmployee/EmployeeGrid/types';
import {
  OpenRequestsDays,
  OpenRequestsRow
} from 'components/shared/roster/RosterViewByEmployee/OpenRequestsGrid/types';
import {
  OpenDuty,
  PlannedDuty
} from 'components/shared/roster/RosterViewByEmployee/types';
import { Interval, PlanUnit } from 'components/types';

import { fromISOString, getMonthInterval } from 'utils/date';
import { getLocalState, updateLocalState } from 'utils/localStorage';

import { getTeamRosterData } from 'RestAPIs/roster';

import RosterViewByEmployee from 'components/shared/roster/RosterViewByEmployee/RosterViewByEmployee';

const getData = (interval: Interval, selectedPlanUnits: PlanUnit[]) =>
  new Promise<{
    employees: Employee[];
    openRequestsRows: OpenRequestsRow[];
  }>((resolve, reject) => {
    getTeamRosterData(interval, selectedPlanUnits, true).then(
      ({ data }) => {
        const { benefitCostCenters, duties, absences } = data;
        const plannedDuties: PlannedDuty[] = [];
        const openDuties: OpenDuty[] = [];
        duties.forEach((duty) => {
          if (duty.isOpen) {
            openDuties.push(duty);
          } else {
            plannedDuties.push(duty);
          }
        });
        resolve({
          employees: benefitCostCenters.map(
            ({ benefit, plannedCostCenter, types }) => {
              const days = {} as EmployeeDays;
              plannedDuties
                .filter(
                  (duty) =>
                    duty.benefitId === benefit.id &&
                    (duty.costCenter?.id === plannedCostCenter.id ||
                      duty.isFromDifferentCostCenter)
                )
                .forEach((duty) => {
                  const { date } = duty;
                  if (days[date]) {
                    const { duties } = days[date];
                    if (duties) {
                      duties.push(duty);
                    } else {
                      days[date].duties = [duty];
                    }
                  } else {
                    days[date] = {
                      duties: [duty]
                    };
                  }
                });

              absences
                .filter(
                  (absence) =>
                    absence.workAgreementId === benefit.workAgreement.id
                )
                .forEach((absence) => {
                  const { start, end } = absence;
                  eachDayOfInterval({
                    start: fromISOString(start),
                    end: new Date(fromISOString(end).getTime() - 1000)
                  }).forEach((date) => {
                    const dateStr = format(date, 'yyyy-MM-dd');
                    if (days[dateStr]) {
                      const { absences } = days[dateStr];
                      if (absences) {
                        absences.push(absence);
                      } else {
                        days[dateStr].absences = [absence];
                      }
                    } else {
                      days[dateStr] = {
                        absences: [absence]
                      };
                    }
                  });
                });

              return {
                ...benefit.workAgreement.employee,
                plannedCostCenter,
                types,
                benefit,
                days
              };
            }
          ),

          openRequestsRows: values(
            groupBy(openDuties, (duty) => [
              duty.costCenter.id,
              duty.function.id
            ])
          ).map((duties) => {
            const days = {} as OpenRequestsDays;

            duties.forEach((duty) => {
              const { date } = duty;
              if (days[date]) {
                days[date].push(duty);
              } else {
                days[date] = [duty];
              }
            });

            return {
              id: `${duties[0].function.id}-${duties[0].costCenter.id}`,
              function: duties[0].function,
              planUnitName: duties[0].costCenter.name,
              days
            };
          })
        });
      },
      () => {
        reject();
      }
    );
  });

type Props = {
  portal: 'employee' | 'roster';
  onRosterViewChanged: (rosterView: 'employees' | 'duties') => void;
};

export default function TeamRosterViewByEmployee({
  portal,
  onRosterViewChanged
}: Props) {
  const localState = getLocalState().employeePortal?.teamRoster;
  const [selectedPlanUnits, setSelectedPlanUnits] = useState<PlanUnit[]>([]);
  const [showOnlyPlanned, setShowOnlyPlanned] = useState(
    localState?.employees?.showOnlyPlanned || false
  );

  const onPlanUnitSelected = (value: PlanUnit[]) => {
    updateLocalState({
      employeePortal: {
        teamRoster: {
          lastSelectedPlanUnitsIds: value.map(({ uniqId }) => uniqId)
        }
      }
    });
    setSelectedPlanUnits(value);
  };

  const onShowOnlyPlanned = () => {
    const value = !showOnlyPlanned;
    setShowOnlyPlanned(value);
    updateLocalState({
      employeePortal: {
        teamRoster: {
          employees: {
            showOnlyPlanned: value
          }
        }
      }
    });
  };

  return (
    <RosterViewByEmployee
      getData={getData}
      isAccordion
      selectedPlanUnits={selectedPlanUnits}
      lastSelectedPlanUnitsIds={
        window.teamRosterTempCostCenters?.map(({ brId }) => brId) ||
        localState?.lastSelectedPlanUnitsIds
      }
      onPlanUnitSelected={onPlanUnitSelected}
      showOnlyPlanned={showOnlyPlanned}
      onShowOnlyPlanned={onShowOnlyPlanned}
      defaultInterval={
        window.teamRosterTempDate
          ? getMonthInterval(fromISOString(window.teamRosterTempDate))
          : undefined
      }
      portal={portal}
      onRosterViewChanged={onRosterViewChanged}
    />
  );
}
